import { useCallback, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { McpServerInput } from "@krut/shared";
import { api, unwrap } from "../api.ts";
import { Badge, Button, Card, ErrorText, PageHeader, Spinner } from "../components/ui.tsx";
import { AUTH_LABEL, ConnectionBadge, ServerDialog, ServerRow, ToolsDialog, useOAuthResult, type McpServer } from "../components/mcp.tsx";

type AdminServer = McpServer & { peopleConnected: number };
type PersonalServer = { id: string; name: string; url: string; auth: McpServer["auth"]; enabled: boolean; owner: string; createdAt: string };

/** Company MCP servers every assistant gets, and an overview of the servers people added themselves. */
export function AdminMcpPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-mcp"], queryFn: () => unwrap(api.admin.mcp.$get()) });
  const refresh = () => void qc.invalidateQueries({ queryKey: ["admin-mcp"] });
  const oauth = useOAuthResult();
  const [editing, setEditing] = useState<McpServer | null | "new">(null);
  const [testing, setTesting] = useState<McpServer | null>(null);

  const connect = useMutation({
    mutationFn: ({ s, headers }: { s: McpServer; headers?: Record<string, string> }) => unwrap(api.admin.mcp[":id"].connect.$post({ param: { id: s.id }, json: { headers } })),
    onSuccess: (r) => (r.authorizationUrl ? (location.href = r.authorizationUrl) : refresh()),
  });
  const disconnect = useMutation({
    mutationFn: (id: string) => unwrap(api.admin.mcp[":id"].connection.$delete({ param: { id } })),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => unwrap(api.admin.mcp[":id"].$delete({ param: { id } })),
    onSuccess: refresh,
  });
  const runTest = useCallback((id: string) => unwrap(api.admin.mcp[":id"].test.$post({ param: { id } })), []);

  const save = async (input: McpServerInput, headerValues: Record<string, string> | null) => {
    const s = (editing && editing !== "new"
      ? await unwrap(api.admin.mcp[":id"].$put({ param: { id: editing.id }, json: input }))
      : await unwrap(api.admin.mcp.$post({ json: input }))) as McpServer;
    refresh();
    if (s.credentials !== "shared") return;
    if (headerValues) await connect.mutateAsync({ s, headers: headerValues });
    else if (s.auth === "oauth" && s.connection?.status !== "connected") await connect.mutateAsync({ s });
  };

  if (q.isPending) return <div className="grid place-items-center py-16"><Spinner /></div>;
  if (q.error) return <ErrorText error={q.error} />;
  const { servers, personal } = q.data as { servers: AdminServer[]; personal: PersonalServer[] };

  return (
    <>
      <PageHeader
        title="MCP servers"
        description="Company servers are available to every assistant. Sign in once with a shared account, or let each person connect their own. Credentials stay encrypted here and never reach an assistant."
        actions={<Button variant="primary" onClick={() => setEditing("new")}>Add a server</Button>}
      />
      {(oauth.connected || oauth.error) && (
        <Card className={oauth.connected ? "mb-4 border-emerald-300 bg-emerald-50 p-4 text-sm text-emerald-800" : "mb-4 border-red-300 bg-red-50 p-4 text-sm text-red-800"}>
          {oauth.connected ? `Connected ${oauth.connected}. Assistants pick it up within a few minutes.` : `Couldn't connect: ${oauth.error}`}
        </Card>
      )}
      <ErrorText error={connect.error ?? disconnect.error ?? remove.error} />

      <Card className="mb-8 divide-y divide-[var(--line)]">
        {servers.length === 0 && <p className="px-5 py-8 text-center text-sm text-[var(--muted)]">No company servers yet.</p>}
        {servers.map((s) => {
          const shared = s.credentials === "shared" && !!s.connection;
          const st = s.connection?.status;
          return (
            <ServerRow
              key={s.id}
              s={s}
              badge={s.connection && !shared ? <Badge>{s.peopleConnected} connected</Badge> : <ConnectionBadge s={s} />}
              actions={
                <>
                  {shared && st !== "connected" && (
                    <Button className="h-8" variant="primary" disabled={connect.isPending} onClick={() => (s.auth === "headers" ? setEditing(s) : connect.mutate({ s }))}>
                      {s.auth === "headers" ? "Add keys" : st === "expired" || st === "error" ? "Sign in again" : "Sign in"}
                    </Button>
                  )}
                  <Button className="h-8" variant="ghost" disabled={shared && st !== "connected"} onClick={() => setTesting(s)}>Test</Button>
                  <Button className="h-8" variant="ghost" onClick={() => setEditing(s)}>Edit</Button>
                  {shared && st === "connected" && (
                    <Button className="h-8" variant="ghost" onClick={() => confirm(`Disconnect the shared account from ${s.name}? Assistants lose it until you connect again.`) && disconnect.mutate(s.id)}>Disconnect</Button>
                  )}
                  <Button className="h-8" variant="ghost" onClick={() => confirm(`Remove ${s.name} from every assistant? They restart briefly.`) && remove.mutate(s.id)}>Remove</Button>
                </>
              }
            />
          );
        })}
      </Card>

      <h2 className="mb-1 text-base font-semibold">Added by people</h2>
      <p className="mb-3 text-sm text-[var(--muted)]">Private servers people added for their own assistant (public https only). Their credentials aren't visible here.</p>
      <Card className="divide-y divide-[var(--line)]">
        {personal.length === 0 && <p className="px-5 py-6 text-center text-sm text-[var(--muted)]">None.</p>}
        {personal.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center gap-4 px-5 py-3">
            <div className="min-w-0 flex-1">
              <div className="font-medium">{p.name} {!p.enabled && <Badge>off</Badge>}</div>
              <div className="truncate text-xs text-[var(--muted)]">{p.owner} · {p.url} · {AUTH_LABEL[p.auth]}</div>
            </div>
            <Button className="h-8" variant="ghost" onClick={() => confirm(`Remove ${p.owner}'s server ${p.name}? Their assistant restarts briefly.`) && remove.mutate(p.id)}>Remove</Button>
          </div>
        ))}
      </Card>

      <ServerDialog open={editing !== null} onClose={() => setEditing(null)} company initial={editing === "new" ? null : editing} onSave={save} />
      <ToolsDialog server={testing} onClose={() => setTesting(null)} run={runTest} />
    </>
  );
}
